'use client';

import type { FC } from 'react';
import { cn } from '../../utils/cn';
import { Credits } from '../../components/Credits';
import { Avatar, List } from '../../components/ui';
import { useNavigation } from '../../contexts/NavigationContext';
import { ReferralRewardStatus, type Referral } from '../../services/generated/Api';
import { FaTrophy, FaClock, FaTimesCircle } from '../../components/icons';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useDisplayUserName } from '../../hooks/useDisplayUserName';
import translations from './ReferralScreen.translations';

interface ReferralListProps {
  referrals: Referral[];
}

export const ReferralList: FC<ReferralListProps> = ({ referrals }) => {
  const navigation = useNavigation();
  const { t } = useLocalTranslation(translations);
  const displayUserName = useDisplayUserName();

  const handleReferralClick = (userId: string) => {
    navigation.navigate('profile', { userId });
  };

  return (
    <div className="px-4">
      <List>
        {referrals.map((referral, i) => {
        const name = displayUserName(referral.firstName, referral.lastName);
        const isLast = i === referrals.length - 1;
        const onReview =
          referral.activated &&
          (referral.rewardStatus === ReferralRewardStatus.Held || referral.rewardStatus === ReferralRewardStatus.Review);
        const isRejected = referral.activated && referral.creditsAwarded === 0 && !onReview;
        return (
          <div
            key={referral.id}
            onClick={() => handleReferralClick(referral.id)}
            className={cn(
              'flex items-center gap-3 ps-3 pe-[10px] min-h-[60px] relative cursor-pointer transition-colors active:bg-white/[0.05]',
              !isLast && 'after:content-[""] after:absolute after:start-[56px] after:end-[10px] after:bottom-0 after:h-px after:bg-white/[0.07]',
            )}
          >
            <Avatar
              src={`/api/users/avatar/${referral.id}`}
              fallback={name.charAt(0).toUpperCase()}
              alt={name}
              size="s"
            />

            <div className="flex-1 min-w-0 flex flex-col py-2">
              <span className="text-[15px] leading-[18px] text-label-primary truncate">{name}</span>
              {!referral.activated && (
                <span className="text-[13px] leading-[16px] text-label-tertiary truncate">{t('pendingActivation')}</span>
              )}
              {isRejected && (
                <span className="text-[13px] leading-[16px] text-label-tertiary truncate">{t('activationRejected')}</span>
              )}
              {onReview && (
                <span className="text-[13px] leading-[16px] text-label-tertiary truncate">{t('rewardOnReview')}</span>
              )}
            </div>

            <div className="shrink-0 flex items-center gap-1.5">
              {referral.isBonus && <FaTrophy className="text-base text-accent-yellow" />}
              {onReview ? (
                <span className="text-[15px] font-semibold text-label-tertiary">
                  +<Credits amount={referral.pendingCredits} />
                </span>
              ) : referral.activated && !isRejected ? (
                <span className={cn('text-[15px] font-semibold', referral.isBonus ? 'text-accent-yellow' : 'text-accent-green')}>
                  +<Credits amount={referral.creditsAwarded} />
                </span>
              ) : isRejected ? (
                <FaTimesCircle className="text-base text-label-quaternary" />
              ) : (
                <FaClock className="text-base text-label-quaternary" />
              )}
            </div>
            </div>
          );
        })}
      </List>
    </div>
  );
};
